import { RecurringFileList, ValidationRecurringBody } from '@back-office/http';
import React, { useEffect, useState } from 'react';
import { useToggle } from 'react-use';
import { ApproveRecurringListProps, BatchProps } from '.';
import { FileTypeProps } from '../approve-recurring-file-dialog';

type UseViewModelProps = {
  data: RecurringFileList[];
  multiselect: boolean;
  handleCheckListRecurringFile: (data: {
    onboardBatchID: string[];
  }) => Promise<ValidationRecurringBody>;
  isCheckerPermission: string;
  isAdmin: boolean;
};

type UseViewModelReturn = {
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  approveDialog: boolean;
  toggleApproveDialog: (value: boolean) => void;
  rejectDialog: boolean;
  toggleRejectDialog: (value: boolean) => void;
  approveRecurringLists: ApproveRecurringListProps;
  setApproveRecurringLists: React.Dispatch<
    React.SetStateAction<ApproveRecurringListProps>
  >;
  handleSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleClick: (id: string) => void;
  isSelected: (id: string) => boolean;
  statusConverter: Record<string, string>;
  findBatchNameFormId: (id: string[]) => string[];
  recombineBatchNameFromId: (batch: BatchProps) => FileTypeProps;
  getFileStatusInfo: (from: string, itm?: string[]) => Promise<void>;
  isShowButton: (item: RecurringFileList) => boolean;
  approveDialogOpenTime: string;
  setApproveDialogOpenTime: (value: string) => void;
};

const useViewModel = ({
  data,
  multiselect,
  handleCheckListRecurringFile,
  isCheckerPermission,
  isAdmin,
}: UseViewModelProps): UseViewModelReturn => {
  const [selected, setSelected] = useState<string[]>([]);
  const [approveDialog, toggleApproveDialog] = useToggle(false);
  const [rejectDialog, toggleRejectDialog] = useToggle(false);
  const [approveRecurringLists, setApproveRecurringLists] =
    useState<ApproveRecurringListProps>({
      showTab: false,
      allpass: false,
      approveLists: [],
      failedLists: [],
    });

  const [approveDialogOpenTime, setApproveDialogOpenTime] =
    useState<string>('');

  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.checked) {
      const newSelected = data.map((item) => item.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (id: string): void => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id: string): boolean => selected.indexOf(id) !== -1;

  const statusConverter = {
    'upload processing': 'upload_processing',
    'upload rejected': 'rejected',
    'failed to upload': 'failed_to_upload',
    'pending approval': 'pending',
    'partial approved': 'partial',
    'failed to approve': 'failed_to_approve',
    'in queue': 'in_queue',
    'ongoing processing': 'ongoing',
    'processing failed': 'failed',
    'processing completed': 'completed',
  };

  useEffect(() => {
    if (multiselect) {
      setSelected([]);
    }
  }, [multiselect]);

  const findBatchNameFormId = (id: string[]): string[] => {
    const batchNames = [];

    data.forEach((file) => {
      if (id.includes(file.id)) {
        batchNames.push(file.batch_name);
      }
    });
    return batchNames;
  };

  const recombineBatchNameFromId = (batch: BatchProps): FileTypeProps => {
    const filterBatch = data.filter((file) => file.id === batch.onboardBatchID);
    if (Object.values(filterBatch).length > 0) {
      return {
        file_name: filterBatch[0]?.batch_name,
        id: filterBatch[0]?.id,
      };
    }
    return { file_name: 'N/A', id: batch.onboardBatchID };
  };

  const getFileStatusInfo = async (
    from: string,
    itm?: string[]
  ): Promise<void> => {
    const trueResults = [];
    const falseResults = [];
    const filePayload = { onboardBatchID: from === 'single' ? itm : selected };
    try {
      const { onboardBatch, Summary } = await handleCheckListRecurringFile(
        filePayload
      );

      onboardBatch?.forEach((batch) => {
        if (batch.result) {
          trueResults.push(recombineBatchNameFromId(batch));
        } else {
          falseResults.push(recombineBatchNameFromId(batch));
        }
      });

      setApproveRecurringLists({
        showTab: (trueResults?.length > 0 && falseResults?.length > 0) || false,
        allpass: Summary?.failed === 0 || false,
        approveLists: trueResults,
        failedLists: falseResults,
      });

      toggleApproveDialog(true);
    } catch (error) {
      toggleApproveDialog(false);
    }
  };

  const isShowButton = (item: RecurringFileList): boolean =>
    item.status === 'Pending approval' ||
    (item.status === 'Partial approved' &&
      (item.perm_is_approved !== isCheckerPermission || isAdmin));

  useEffect(() => {
    if (approveDialog) {
      const date = new Date();
      const isoString = date.toISOString();
      setApproveDialogOpenTime(isoString);
    }
  }, [approveDialog]);

  return {
    selected,
    setSelected,
    approveDialog,
    toggleApproveDialog,
    rejectDialog,
    toggleRejectDialog,
    approveRecurringLists,
    setApproveRecurringLists,
    handleSelectAllClick,
    handleClick,
    isSelected,
    statusConverter,
    findBatchNameFormId,
    recombineBatchNameFromId,
    getFileStatusInfo,
    isShowButton,
    approveDialogOpenTime,
    setApproveDialogOpenTime,
  };
};

export default useViewModel;
